/**
 * "A link may say this once": the one way a portal reads a CHOICE a link names
 * in its query string (`?lang=`, `?org=`, `?dev=`, `?devAuth=`) and takes it
 * back out.
 *
 * Not the guest's scanned apartment code. `?q=`, `?qr=` and `/q/<token>` are
 * read by `apps/guest-web/src/lib/qr-url.ts` and scrubbed by its
 * `scrubQrFromAddressBar`, for two reasons this module does not share: the code can be
 * in the PATH, which a query-string reader never sees, and it is a bearer
 * credential, so the guest drops the entry's state (`replaceState(null, ...)`)
 * rather than carrying it forward, and its way out of a revoked code
 * (`forget`) scrubs the address bar whatever it holds.
 *
 * Owns: reading the query string, handing it whole to the caller's parser, and -
 * when the parser accepted something - removing exactly that one word from the
 * URL with `history.replaceState`. Refuses: deciding what a value means (the
 * parser is the caller's, pinned by the caller's own tests) and remembering it
 * (the caller writes its own store, under its own key). Nothing here throws.
 *
 * WHY A REQUEST IS BOTH WRITTEN AND STRIPPED. A link's `?lang=`, `?org=`,
 * `?dev=` or `?devAuth=` outranks whatever the browser remembers, because it is
 * the most recent thing the visitor said, so the caller stores it as a choice:
 * without the write a reload would forget it. Without the strip the query would
 * re-impose itself on every reload afterwards - a founder who switched language
 * or organization in the portal would be moved back by pressing refresh.
 *
 * WHY ONLY ITS OWN WORD. The partner portal reads three of these on one landing
 * (`/sign-up?dev=part_1:new&lang=en&org=property`), each from whatever the
 * address bar says when its reader runs. Each removes its own word and leaves
 * the others and the hash untouched, so the order the readers run in does not
 * matter.
 *
 * WHY `history.state` IS PASSED THROUGH. `installHistoryMemory()` runs in the
 * partner, internal and bakery portals and keeps its scroll mark in the history
 * entry's state; a `replaceState(null, ...)` would erase it on the very entry
 * the link landed on.
 *
 * WHY THE VALUE SURVIVES A FAILED STRIP. A URL this browser will not parse, or
 * a host with no history to rewrite, is not a reason to lose what the link just
 * named - a counter, an identity, a language. The query simply stays in the
 * address bar, which costs one re-imposition on reload; losing the value costs
 * the landing.
 */

/** Where a link's request is read from and taken out of. Structural, so a test needs no browser. */
export interface AddressBar {
  readonly location?: { readonly href: string; readonly search: string };
  readonly history?: {
    readonly state?: unknown;
    replaceState(data: unknown, unused: string, url?: string | URL | null): void;
  };
}

/**
 * Reads what a link asked for, ONCE. `parse` takes the whole query string and
 * answers null for anything it does not accept, in which case nothing is
 * touched. Otherwise the value is returned AND exactly `name` is taken out of
 * the URL, keeping the history entry's own state - unless the URL does not
 * carry `name` at all (a parser may infer a value from another word, as
 * Internal's `?dev=` implies `devAuth`), when there is nothing to rewrite. The value is returned even if
 * the strip fails. The caller persists it.
 */
export function consumeSearchParam<T>(
  name: string,
  parse: (search: string) => T | null,
  bar: AddressBar = globalThis,
): T | null {
  const location = bar.location;
  if (location === undefined) return null;
  const value = parse(location.search);
  if (value === null) return null;
  try {
    const url = new URL(location.href);
    if (url.searchParams.has(name)) {
      url.searchParams.delete(name);
      bar.history?.replaceState(bar.history.state ?? null, '', url.toString());
    }
  } catch {
    // The query simply stays in the address bar (see the header).
  }
  return value;
}
